// src/components/settings/shared/ImageUpload.jsx
import React from 'react';
import { FileUpload } from '../../common/FileUpload';
import { firestoreService } from '../../../services/firestoreService';

export const ImageUpload = ({ 
  value, 
  onChange, 
  onUpload,
  uploadStates = {},
  errors = {},
  bookId,
  fieldKey,
  label,
  description,
  accept = "image/*",
  maxSize = 5, // MB
  aspectRatio, // "16:9", "1:1", "4:3"
  showPreview = true,
  placeholder = "Upload image"
}) => {
  // Handle file upload with proper error handling and state management
  const handleFileUpload = async (file, error) => {
    if (error) {
      // Update error state if there's a validation error
      if (onChange && fieldKey) {
        onChange(`${fieldKey}_error`, error);
      }
      return;
    }

    if (!file || !bookId) return;

    try {
      // Update upload state to show uploading
      if (onChange && fieldKey) {
        onChange(`${fieldKey}_uploading`, true);
        onChange(`${fieldKey}_error`, null);
      }

      // Construct storage path similar to other template settings
      const storagePath = `pb-${bookId}/images/${fieldKey}`;
      
      // Upload file using firestoreService
      const result = await firestoreService.uploadFile(file, storagePath);
      
      // Update the form data with the uploaded image URL
      if (onChange) {
        onChange(fieldKey, result.url);
      }

      // Update upload state to success
      if (onChange && fieldKey) {
        onChange(`${fieldKey}_uploading`, false);
        onChange(`${fieldKey}_error`, null);
      }

      console.log(`${fieldKey} uploaded successfully:`, result);
      
    } catch (uploadError) {
      console.error(`Error uploading ${fieldKey}:`, uploadError);
      
      // Update error state
      if (onChange && fieldKey) {
        onChange(`${fieldKey}_uploading`, false);
        onChange(`${fieldKey}_error`, uploadError.message || 'Upload failed');
      }
    }
  };

  // Handle file removal
  const handleRemove = () => {
    if (onChange && fieldKey) {
      onChange(fieldKey, '');
      onChange(`${fieldKey}_error`, null);
    }
  };

  // Get upload state from uploadStates object
  const getUploadState = () => {
    if (!fieldKey || !uploadStates) return false;
    return uploadStates[`${fieldKey}_uploading`] || uploadStates[fieldKey]?.uploading || false;
  };

  // Get error state from errors object
  const getError = () => {
    if (!fieldKey) return null;
    return errors[`${fieldKey}_error`] || errors[fieldKey] || uploadStates[fieldKey]?.error || null;
  };

  // Get size recommendation based on aspect ratio
  const getSizeRecommendation = () => {
    if (aspectRatio === '16:9') return '1200x675px recommended';
    if (aspectRatio === '1:1' || aspectRatio === 'square') return 'Square format, 400x400px recommended';
    if (aspectRatio === '4:3') return '800x600px recommended';
    return `Max ${maxSize}MB`;
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700">{label}</label>
      )}
      {description && (
        <p className="text-sm text-gray-500">{description}</p>
      )}
      
      <FileUpload
        onFileSelect={handleFileUpload}
        currentFile={value}
        onRemove={handleRemove}
        accept={accept}
        maxSize={maxSize}
        label={placeholder}
        uploading={getUploadState()}
        error={getError()}
        showPreview={showPreview}
        aspectRatio={aspectRatio}
        className="w-full"
      />
      
      {/* Additional info based on aspect ratio */}
      <p className="text-xs text-gray-500">
        {getSizeRecommendation()}
      </p>
      
      {/* Show current file info if available */}
      {value && !getUploadState() && (
        <div className="text-xs text-green-600 flex items-center gap-1">
          <span>✓</span>
          Image uploaded successfully
        </div>
      )}
    </div>
  );
};

export default ImageUpload;